// Map UX quest (2026-09-17) - cluster sizing and the ring ripple.
// Pure values/expressions/selection only, so they can be unit-tested; the
// animation loop lives in src/hooks/useMarkerPulse.ts.
//
// History, same day (git keeps the code):
//   v1 a FILLED halo behind every marker - blurred together in dense views.
//   v2 a separate expanding 2px ring on at most 12 markers.
//   v3 (this) founder correction: no separate halo layer at all. The cluster
//      icon PNGs (farmers.png / trees.png) already carry three concentric white
//      rings; those rings are rebuilt as three layers and their OPACITY ripples
//      outward (inner, then middle, then outer). No change in size.
import type { Expression } from 'mapbox-gl';

// Colours sampled from the marker PNGs (src/assets/svg/farmers.png, trees.png).
export const MARKER_COLOURS = { farmers: '#DB9030', forests: '#84A341' } as const;
export type MarkerKind = keyof typeof MARKER_COLOURS;

// Clusters carry no count text any more, so SIZE is the only signal of how many
// markers a cluster holds: [minimum point_count, core radius in px].
export const CLUSTER_RADIUS_STEPS: ReadonlyArray<readonly [number, number]> = [
  [0, 18],
  [10, 22],
  [50, 27],
  [200, 33],
  [1000, 40],
];

// The three rings, measured in the PNG (core radius 20px): each ring is 2px
// wide, starting at 20, 24 and 28px, white at opacity 1.0 / 0.6 / 0.3. Kept as
// proportions of the core radius so they scale with the stepped cluster size.
export const RINGS = [
  { inner: 1.0, width: 0.1, staticOpacity: 1.0 },
  { inner: 1.2, width: 0.1, staticOpacity: 0.6 },
  { inner: 1.4, width: 0.1, staticOpacity: 0.3 },
] as const;

// 2026-09-18 (founder correction, second round): the ORIGINAL brief asked for a
// pulse behind every cluster circle, farmer pin AND tree pin - the 2c rework
// covered clusters only (their icon carries 3 rings) and silently dropped
// individual pins, which was never a decision, just an oversight. farmer.png /
// tree.png (72x81) were decoded the same way: one opaque white ring around the
// badge, centre (35.5, 26) - NOT the image's geometric centre (36, 40.5), because
// the teardrop tail extends below the badge. Mapbox's default icon-anchor is
// 'center', so the geo point sits at the image centre and the ring layer needs a
// screen-pixel translate to land on the badge: dy = (40.5 - 26) * icon-size 1.2
// = 17.4px up. Radius ~26 image-px * 1.2 = ~31 screen-px. Verified by eye in the
// browser (docs/pr-screenshots/2026-09-17-map-ux/pin-ring-alignment-check.png)
// before this was trusted.
// Founder 2026-09-21: staticOpacity 0 (was 1.0). The single pin's ring is
// invisible at rest for both farmers and forests, and only the hovered /
// selected pin ripples it in (useMarkerPulse -> pinRingOpacityExpression).
export const PIN_RING = { radius: 31, width: 3, staticOpacity: 0, translateY: -17 } as const;

export const RIPPLE_PERIOD_MS = 2500;
// Inner ring peaks first, then middle, then outer.
export const RIPPLE_STAGGER_MS = 400;
export const RIPPLE_MIN_OPACITY = 0.15;
export const RIPPLE_MAX_OPACITY = 0.6;
// Half-width of each ring's brightening, and the fade between the static look
// and the ripple when a marker starts or hands off (so nothing pops).
const RIPPLE_BUMP_MS = 600;
const BLEND_MS = 500;

// 2026-09-18 (founder correction): "one halo at a time, not all pins haloing
// at the same time" - ONE marker ripples across the whole view, not a pool of
// several at once. MAX_ACTIVE_MARKERS is kept as a named constant (not a bare
// 1 scattered through the code) so nextActive()'s "at most N, hand off to
// another" contract stays generic - only this one number changed, from an
// original 12 down to the founder's corrected 1.
export const MAX_ACTIVE_MARKERS = 1;
// Each marker ripples for about two cycles, then hands off to another in view.
export const CYCLES_PER_MARKER = 2;
// Ripple strength: full at <= 10 markers (clusters + pins) in view, linearly
// down to 0 at >= 60 - one shared population, per the original brief ("every
// cluster circle, farmer pin and tree pin"). Still applies with a single active
// marker: in a dense view its one ripple fades toward invisible, same as before.
export const DENSITY_FULL = 10;
export const DENSITY_NONE = 60;
// Founder 2026-09-19 (card views): was 17. Moves with clusterMaxZoom in
// Layers.tsx (now 15) so no ripple is attempted where clusters no longer exist.
export const FORESTS_MAX_RIPPLE_ZOOM = 15;
// Founder 2026-09-19 (card views): at high zoom the dense Mizan planting broke
// into hundreds of tiny clusters whose white halo rings overlapped into noise.
// The three cluster rings (NOT the individual pin's ring) fade linearly from
// their full opacity at HALO_FADE_FULL_ZOOM to zero at HALO_FADE_ZERO_ZOOM.
// The core disc keeps its size, so the count signal survives the fade.
export const HALO_FADE_FULL_ZOOM = 11;
export const HALO_FADE_ZERO_ZOOM = 13;

// Mapbox only accepts ['zoom'] as the input of a TOP-LEVEL interpolate/step,
// so the fade wraps the opacity expression rather than multiplying into it.
export const haloZoomFade = (opacity: Expression | number): Expression =>
  [
    'interpolate',
    ['linear'],
    ['zoom'],
    HALO_FADE_FULL_ZOOM,
    opacity,
    HALO_FADE_ZERO_ZOOM,
    0,
  ] as Expression;

// Founder 2026-09-20 (staging review, map-ux-2): clusters go QUIET as you zoom
// in. On top of the ring fade above, the core disc (and its rings, before they
// vanish) shrink linearly from the stepped size at CLUSTER_SHRINK_START_ZOOM to
// a plain CLUSTER_MIN_RADIUS px dot at CLUSTER_SHRINK_END_ZOOM (= clusterMaxZoom
// in Layers.tsx, where clusters hand off to individual pins). Same top-level
// zoom-interpolate rule as haloZoomFade.
export const CLUSTER_SHRINK_START_ZOOM = 11;
export const CLUSTER_SHRINK_END_ZOOM = 15;
export const CLUSTER_MIN_RADIUS = 5;

export const clusterZoomShrink = (full: Expression, small: Expression | number): Expression =>
  [
    'interpolate',
    ['linear'],
    ['zoom'],
    CLUSTER_SHRINK_START_ZOOM,
    full,
    CLUSTER_SHRINK_END_ZOOM,
    small,
  ] as Expression;

// The stepped size on its own (count -> px), used inside the zoom shrink.
export const clusterRadiusExpression = (): Expression => {
  const [[, first], ...rest] = CLUSTER_RADIUS_STEPS;
  return ['step', ['get', 'point_count'], first, ...rest.flat()] as Expression;
};

// The core disc as drawn: stepped by count, shrinking with zoom.
export const clusterDiscRadiusExpression = (): Expression =>
  clusterZoomShrink(clusterRadiusExpression(), CLUSTER_MIN_RADIUS);

// Ring k is drawn as a transparent circle of radius `inner` with a stroke of
// `width` (Mapbox draws the stroke outside the radius). Both scale with the
// disc, so they shrink with it too.
export const ringRadiusExpression = (k: number): Expression =>
  clusterZoomShrink(
    ['*', clusterRadiusExpression(), RINGS[k].inner] as Expression,
    CLUSTER_MIN_RADIUS * RINGS[k].inner,
  );
export const ringWidthExpression = (k: number): Expression =>
  clusterZoomShrink(
    ['*', clusterRadiusExpression(), RINGS[k].width] as Expression,
    CLUSTER_MIN_RADIUS * RINGS[k].width,
  );

// Founder 2026-09-20 (staging review): single TREE pins get no halo ring at any
// zoom. Measured on staging 2026-09-20 at zoom 16-18 on the Mizan view: zero
// clusters on screen, 2,666 / 1,541 / 674 rendered `forests-pin-ring` circles
// against only 12 / 10 / 15 drawn tree icons - the symbol layer hides colliding
// icons, the circle layer draws every ring, hence the "wall of white rings".
// Founder 2026-09-21 (production review): no static halo on ANY single pin,
// farmers included, and the icon images lost their baked-in ring as well
// (farmer-noring.png / tree-noring.png). The ring layer is mounted for both
// kinds again, but at PIN_RING.staticOpacity 0: it is invisible at rest and
// only the hovered / selected pin's ring ripples in (0.15 to 0.6), so the
// zoom 16-18 wall cannot come back - a ring at opacity 0 draws nothing.
export const PIN_RING_KINDS: ReadonlyArray<MarkerKind> = ['farmers', 'forests'];

// Founder 2026-09-21 (production review): the shrunken dots were invisible
// against the forest. Clusters keep the marker colour at full size (zoom
// CLUSTER_SHRINK_START_ZOOM and below) and turn brand yellow with a 1px dark
// outline as they shrink toward the split; single trees between the split
// and FOREST_PIN_MIN_ZOOM are the same yellow dots (Layers.tsx `forests-dots`);
// tree pins from FOREST_PIN_MIN_ZOOM keep their green icon.
export const DOT_COLOUR = '#ffd900';
export const DOT_OUTLINE_COLOUR = '#333333';
export const DOT_OUTLINE_WIDTH = 1;
// Where the unclustered single trees switch from yellow dot to green tree pin.
// Founder decision 2026-09-21 (map-ux-4, Question 35 = option a): 16.5, which
// sits BETWEEN the two tree cards' landing zooms so each shows a different face:
//   - "Trees, Ethiopia" lands at 16.25 (below 16.5) -> yellow DOTS, pins on zoom in.
//   - "Farm sequestration in Mizan" lands at 16.9 (above 16.5) -> tree PINS.
// (Supersedes the Question 34 answer of 17.)
export const FOREST_PIN_MIN_ZOOM = 16.5;

// Founder 2026-09-21 (production review, round 2): the forests and farmers GROUP
// markers get the same treatment as the pins - a ring-less icon and a ring that
// ripples only on hover / selection (useGroupRipple). forests-group reuses the
// tree pin icon (tree-noring.png), so its ring is PIN_RING; the farmers group
// icon is farmerGroup-noring.png (78x91, disc centre 36,42 r24 vs image centre
// 39,45.5, icon-size 1.2), so its ring is offset and sized here. Factories
// group markers are unchanged (the founder named only forests + farmers).
export const GROUP_RING = { radius: 29, width: 3, translateX: -4, translateY: -4 } as const;
export const GROUP_RING_LAYERS = ['farmers-group', 'forests-group'] as const;

export const clusterColourExpression = (kind: MarkerKind): Expression =>
  clusterZoomShrink(
    MARKER_COLOURS[kind] as unknown as Expression,
    DOT_COLOUR as unknown as Expression,
  );
export const clusterOutlineWidthExpression = (): Expression =>
  clusterZoomShrink(0 as unknown as Expression, DOT_OUTLINE_WIDTH);

// The pin ring's opacity: PIN_RING.staticOpacity (0) at rest, the ripple curve
// (no stagger) while its feature-state says it is hovered / selected.
export const pinRingOpacityExpression = (now: number, density: number): Expression =>
  ringOpacityExpressionFrom(PIN_RING.staticOpacity, 0, now, density);

// The pin's single ring uses the SAME opacity curve as the cluster's inner ring
// (ringOpacityExpression(0, now, density) - no stagger, since there is only one
// ring), but a fixed radius/width: pins don't grow like clusters do.

// 1 at <= 10 in view, linear to 0 at >= 60.
export const densityFactor = (markersInView: number): number => {
  const t = (DENSITY_NONE - markersInView) / (DENSITY_NONE - DENSITY_FULL);
  return Math.min(1, Math.max(0, t));
};

// Forests clusters do not exist above zoom 15 anyway (clusterMaxZoom); the rule
// is explicit so it survives a change to that setting.
export const rippleAllowed = (kind: MarkerKind, zoom: number): boolean =>
  !(kind === 'forests' && zoom > FORESTS_MAX_RIPPLE_ZOOM);

// A marker (cluster or pin) currently rendered in the viewport. Pin ids are
// assigned negative (Layers.tsx) so they can never collide with a supercluster
// cluster_id (always >= 0) when both share one Mapbox feature-state id-space.
export type Candidate = { id: number };
// A cluster that is rippling now. Times are ms on the rAF clock.
export type Active = Candidate & { start: number; end: number };

/**
 * Keeps markers that are still rippling and still in view, and fills the free
 * slots (up to MAX_ACTIVE_MARKERS) with random other clusters in view. A random
 * start delay gives every marker its own phase.
 */
export const nextActive = (
  current: Active[],
  candidates: Candidate[],
  now: number,
  random: () => number = Math.random,
): Active[] => {
  const inView = new Set(candidates.map((c) => c.id));
  const kept = current.filter((a) => a.end > now && inView.has(a.id));
  const used = new Set(kept.map((a) => a.id));
  // A marker that just finished rests for a round when there are others to pick.
  const justEnded = new Set(current.filter((a) => a.end <= now).map((a) => a.id));
  const free = candidates.filter((c) => !used.has(c.id));
  const rested = free.filter((c) => !justEnded.has(c.id));
  const pool = rested.length >= MAX_ACTIVE_MARKERS - kept.length ? rested : free;

  const active = [...kept];
  while (active.length < MAX_ACTIVE_MARKERS && pool.length > 0) {
    const [picked] = pool.splice(Math.floor(random() * pool.length), 1);
    const start = now + random() * RIPPLE_PERIOD_MS;
    active.push({ ...picked, start, end: start + CYCLES_PER_MARKER * RIPPLE_PERIOD_MS });
  }
  return active;
};

// Opacity of ring k at `seconds` into a marker's cycle: RIPPLE_MIN, rising to
// RIPPLE_MAX around the ring's own moment (k * 0.4 s). Plain-number twin of the
// expression below, used by the unit tests to pin the curve down.
export const rippleOpacityAt = (k: number, ms: number): number => {
  const cycle = ((ms % RIPPLE_PERIOD_MS) + RIPPLE_PERIOD_MS) % RIPPLE_PERIOD_MS;
  const raw = Math.abs(cycle - k * RIPPLE_STAGGER_MS);
  const distance = Math.min(raw, RIPPLE_PERIOD_MS - raw);
  const bump = Math.max(0, 1 - distance / RIPPLE_BUMP_MS);
  return RIPPLE_MIN_OPACITY + (RIPPLE_MAX_OPACITY - RIPPLE_MIN_OPACITY) * bump;
};

const state = (name: string): Expression => ['number', ['feature-state', name], 0] as Expression;

/**
 * circle-stroke-opacity for ring k at time `now`. A cluster ripples only while
 * useMarkerPulse has given it a feature-state {start, end}; every other cluster
 * (and every cluster when density is 0) shows the PNG's static opacity.
 */
// The ripple curve for ring k blended from a `fixed` resting opacity: `fixed`
// outside [start, end], easing to the ripple inside. Split out 2026-09-21 so
// the pin ring can rest at 0 (invisible) instead of RINGS[0].staticOpacity.
export const ringOpacityExpressionFrom = (
  fixed: number,
  k: number,
  now: number,
  density: number,
): Expression => {
  const period = RIPPLE_PERIOD_MS;
  // position in the cycle relative to this ring's moment, wrapped to 0..0.5
  const cyclePos = ['%', ['/', ['-', now, state('start')], period], 1];
  const offset = (k * RIPPLE_STAGGER_MS) / period;
  const distance = ['abs', ['-', ['%', ['+', ['-', cyclePos, offset], 1.5], 1], 0.5]];
  const bump = ['max', 0, ['-', 1, ['/', distance, RIPPLE_BUMP_MS / period]]];
  const ripple = ['+', RIPPLE_MIN_OPACITY, ['*', RIPPLE_MAX_OPACITY - RIPPLE_MIN_OPACITY, bump]];
  // 0 outside [start, end], easing to 1 inside, times the density factor
  const blend = [
    '*',
    density,
    [
      'max',
      0,
      [
        'min',
        1,
        ['/', ['-', now, state('start')], BLEND_MS],
        ['/', ['-', state('end'), now], BLEND_MS],
      ],
    ],
  ];
  return ['+', fixed, ['*', blend, ['-', ripple, fixed]]] as Expression;
};

/**
 * circle-stroke-opacity for ring k at time `now`. A cluster ripples only while
 * useMarkerPulse has given it a feature-state {start, end}; every other cluster
 * (and every cluster when density is 0) shows the PNG's static opacity.
 */
export const ringOpacityExpression = (k: number, now: number, density: number): Expression =>
  ringOpacityExpressionFrom(RINGS[k].staticOpacity, k, now, density);

/**
 * circle-stroke-opacity for a CLUSTER ring: ringOpacityExpression wrapped in
 * the zoom fade (founder 2026-09-19). The pin ring keeps the unwrapped
 * ringOpacityExpression - pins never fade.
 */
export const clusterRingOpacityExpression = (k: number, now: number, density: number): Expression =>
  haloZoomFade(ringOpacityExpression(k, now, density));
